import React from 'react';
import { useTranslation } from 'react-i18next';
import { XCircleIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar';
import Modal from '../../shared/components/UI/Modal';
import * as API from '../../api';
import { cn } from '../../shared/utils/cn';

const REASONS = [
  'Mon séjour est annulé',
  'J’ai trouvé moins cher',
  'Je n’ai pas reçu la confirmation à temps',
  'Mes préférences n’étaient pas disponibles',
  'Je n’ai pas réservé pour la bonne date',
  'Je n’ai pas réservé pour le bon nombre de participants',
];

const CancelModal = ({ shown, close, order }) => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = React.useState('');
  const [otherText, setOtherText] = React.useState('');
  const [cancelHelper, setCancelHelper] = React.useState({ type: '', message: '' });
  const [loadingCancel, setLoadingCancel] = React.useState(false);

  const isOther = selected === '__other__';

  const submitCanceling = (event) => {
    event.preventDefault();
    const _form = {
      reservation_id: order.id,
      cancel: 1,
      reason_cancel: isOther ? otherText : selected,
    };
    setLoadingCancel(true);
    API.CancelOrder(_form)
      .then(() => {
        setCancelHelper({ type: 'success', message: t('account.cancelSuccess') });
      })
      .catch((err) => {
        setCancelHelper({ type: 'error', message: err.data.errors });
      })
      .then(() => {
        setLoadingCancel(false);
        setTimeout(() => window.location.reload(), 2000);
      });
  };

  const refundDate = moment((order.date ?? order.date_depart_activity) + ' 12:00')
    .locale(i18n.language)
    .format('LL');

  const canSubmit = selected && (!isOther || otherText.trim());

  return (
    <Modal
      shown={shown}
      close={close}
      size="modal--small"
      title={t('account.cancelTitle')}
      subtitle={t('account.manageSubtitle', { date: refundDate })}
    >
      {cancelHelper.message ? (
        <div
          className={`mb-4 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-sm ${
            cancelHelper.type === 'success'
              ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
              : 'border-rose-100 bg-rose-50 text-rose-600'
          }`}
        >
          {cancelHelper.type === 'success' ? (
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div dangerouslySetInnerHTML={{ __html: cancelHelper.message }} />
        </div>
      ) : null}

      <form onSubmit={submitCanceling} className="space-y-4">
        <p className="text-sm font-medium text-ink">{t('account.cancelReason')}</p>
        <div className="space-y-2">
          {REASONS.map((reason) => (
            <ReasonRow
              key={reason}
              label={reason}
              checked={selected === reason}
              disabled={loadingCancel}
              onClick={() => setSelected(reason)}
            />
          ))}
          <ReasonRow
            label={t('account.cancelOther')}
            checked={isOther}
            disabled={loadingCancel}
            onClick={() => setSelected('__other__')}
          />
          {isOther ? (
            <input
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              disabled={loadingCancel}
              placeholder={t('account.cancelOtherPlaceholder')}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          ) : null}
        </div>

        <button
          type="submit"
          disabled={loadingCancel || !canSubmit}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-sm font-bold text-white shadow-card transition-colors hover:bg-rose-600 disabled:opacity-50"
        >
          {loadingCancel ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <>
              <XCircleIcon className="h-5 w-5" />
              {t('account.cancelConfirm')}
            </>
          )}
        </button>
      </form>
    </Modal>
  );
};

function ReasonRow({ label, checked, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-start text-sm transition-colors',
        checked ? 'border-brand-500 bg-brand-50 text-ink' : 'border-slate-200 text-ink-muted hover:border-brand-300'
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          checked ? 'border-brand-500 bg-brand-500 text-white' : 'border-slate-300'
        )}
      >
        {checked ? <CheckIcon className="h-3 w-3" strokeWidth={3} /> : null}
      </span>
      {label}
    </button>
  );
}

export default CancelModal;
